import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { IPDQueueEntry } from '@/types/ipd';
import { printIPDStickers } from '@/components/ipd/IPDStickerPrint';
import { ensureDoctorPrefix } from '@/lib/doctorName';
import { Printer } from 'lucide-react';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: IPDQueueEntry | null;
};

export default function IPDStickerDialog({ open, onOpenChange, entry }: Props) {
  const [copies, setCopies] = useState(24);

  useEffect(() => {
    if (open) setCopies(24);
  }, [open, entry?.id]);

  if (!entry) return null;

  const consultant = entry.admission?.assignedDoctor
    ? ensureDoctorPrefix(entry.admission.assignedDoctor.name)
    : '—';
  const spec = entry.admission?.assignedDoctor?.specialisation ?? '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Print file stickers</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Stickers use the current admission details, including the consulting doctor name.
          </p>
          <div className="rounded-md border bg-slate-50 p-3 space-y-1">
            <p>
              <span className="font-medium">Patient:</span> {entry.patient?.name ?? '—'}
            </p>
            <p>
              <span className="font-medium">IPD #:</span> {entry.ipdNumber ?? '—'}
            </p>
            <p>
              <span className="font-medium">Consultant:</span> {consultant}
              {spec ? ` (${spec})` : ''}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="sticker-copies">Number of stickers</Label>
            <Input
              id="sticker-copies"
              type="number"
              min={1}
              max={48}
              value={copies}
              onChange={(e) => setCopies(Math.max(1, Math.min(48, Number(e.target.value) || 1)))}
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            type="button"
            onClick={() => {
              printIPDStickers(entry, copies);
              onOpenChange(false);
            }}
          >
            <Printer className="h-4 w-4 mr-2" />
            Print stickers
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
